import isEqual from "lodash/isEqual";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTaxTypes } from "../../api/services/program/programApis";
import { CurrencyFormat } from "../../components/textFieldCurrency/CurrencyUtils";
import { EMPTY_VALUE_DASH } from "../../helpers/constants/common";
import { commonSiteCoreKeys } from "../../helpers/constants/siteCoreKeys";
import {
  getAmountWithFormatByCurrencyName,
  getValueByPercentage,
  getValueWith2DecimalDigits,
  isEmpty,
  isNotEmpty,
} from "../../helpers/utils";
import { useSiteCore } from "../../hooks/useSiteCore";
import { WarningConfirmationModal } from "../modal/WarningModal";
import {
  TaxConfigureTableData,
  TaxDetailsFormKeyProps,
} from "./modelsAndSchemas/Models";
import { TaxDetailsWrapper } from "./TaxDetails.style";
import { TaxDetailsTable } from "./taxDetailsTable/TaxDetailsTable";

interface TaxDetailsProps {
  pageId: string;
  currencyName: string;
  currencyFormat: CurrencyFormat;
  currencyId: string;
  forceUpdate: () => void;
  isInEditMode: boolean;
  taxDetailsFormKeys: TaxDetailsFormKeyProps;
  programOrProjectFee: string;
  isInViewMode?: boolean;
  hasDeletePermission?: boolean;
}

export const TaxDetails = forwardRef((props: TaxDetailsProps, ref) => {
  const {
    pageId,
    currencyName,
    currencyFormat,
    currencyId,
    forceUpdate,
    isInEditMode,
    taxDetailsFormKeys,
    programOrProjectFee,
    isInViewMode = false,
    hasDeletePermission = true,
  } = props;
  const { getValues, setValue, setError } = useFormContext();
  const { fetchValidationMessage, commonTaxDetails, commonLabels } =
    useSiteCore();
  const selectedDeleteTaxRef = useRef<TaxConfigureTableData>();

  const [filteredResults, setFilteredResults] = useState<
    TaxConfigureTableData[]
  >([]);

  const { data: taxList } = useTaxTypes();

  const [onDataAddedCallback, setOnDataAddedCallback] =
    useState<boolean>(false);
  const [onDataDeletedCallback, setOnDataDeletedCallback] =
    useState<boolean>(false);
  const [showDeleteTaxModal, setShowDeleteTaxModal] = useState(false);

  const {
    taxKey,
    taxPercentageKey,
    taxAmountWithFormatKey,
    taxAmountKey,
    taxEntriesKey,
  } = taxDetailsFormKeys;

  useImperativeHandle(ref, () => ({
    resetData,
  }));

  const resetData = () => {
    clearAllValues();
  };

  const clearAllValues = () => {
    setFilteredResults([]);
    clearInputValues();
  };

  const watchTaxEntriesKey = useWatch({
    name: taxEntriesKey,
  });

  useEffect(() => {
    updateTaxEntryOnProgramFee(programOrProjectFee, filteredResults);
  }, [programOrProjectFee, currencyName]);

  const handleTaxDeleteClick = (row: TaxConfigureTableData) => {
    const filteredResultsCopy = [...filteredResults];
    const index = filteredResultsCopy.findIndex((item) => {
      return item.description === row.description;
    });
    filteredResultsCopy.splice(index, 1);

    sortFilterData(filteredResultsCopy);
    setFilteredResults(filteredResultsCopy);
    addEntriesToFormKey(filteredResultsCopy, true);
    setOnDataDeletedCallback(!onDataDeletedCallback);
  };

  const sortFilterData = (filteredResults: TaxConfigureTableData[]) => {
    filteredResults.sort((a, b) => {
      return a.description.localeCompare(b.description);
    });
  };
  useEffect(() => {
    const taxEntriesKeyValue = getValues(taxEntriesKey);

    if (isEmpty(taxEntriesKeyValue)) {
      setFilteredResults([]);
      return;
    }

    if (!isEqual(filteredResults, taxEntriesKeyValue)) {
      const filteredResultsCopy: TaxConfigureTableData[] =
        taxEntriesKeyValue.map((item: TaxConfigureTableData) => {
          return {
            id: item.id,
            taxTypeId: item?.taxType?.id ?? item?.taxTypeId,
            description: item?.description,
            percentage: item?.percentage,
            amount: getValueWith2DecimalDigits(item.amount ?? "0"),
            amountWithFormat: getValueWith2DecimalDigits(item.amount ?? "0"),
            order: item?.order,
          };
        });

      updateTaxEntryOnProgramFee(programOrProjectFee, filteredResultsCopy);
    }
  }, [watchTaxEntriesKey]);

  const addEntriesToFormKey = (
    results: TaxConfigureTableData[],
    isTaxDeleted = false,
  ) => {
    setValue(taxEntriesKey, results, {
      shouldDirty: isTaxDeleted,
    });
    forceUpdate();
  };

  const checkValidations = () => validateTaxEntry();

  const validateTaxEntry = (): TaxConfigureTableData | null => {
    const tax = getValues(taxKey);
    const feePercentage = getValues(taxPercentageKey);
    const amountWithFormat = getValues(taxAmountWithFormatKey);
    const amount = getValues(taxAmountKey);

    validateField(tax, taxKey);

    if (isEmpty(tax)) {
      return null;
    }
    const getTaxData = taxList?.find((taxObj) => taxObj.id === tax);

    return {
      taxTypeId: getTaxData?.id ?? "",
      description: getTaxData?.name ?? "",
      percentage: feePercentage,
      amountWithFormat: amount,
      amount: amountWithFormat,
      order: filteredResults.length + 1,
    };
  };

  const updateTaxEntryOnProgramFee = (
    updatedProgramFee: string,
    filteredResults: TaxConfigureTableData[],
  ) => {
    // If no tax entries and no selected tax, reset amount fields
    if (isEmpty(getValues(taxKey))) {
      setValue(taxAmountWithFormatKey, EMPTY_VALUE_DASH);
      setValue(taxAmountKey, EMPTY_VALUE_DASH);
    } else {
      // Update current tax amount fields based on program fee and percentage
      const taxPercentageValue = getValues(taxPercentageKey);
      const calculatedAmount = getValueWith2DecimalDigits(
        getValueByPercentage(updatedProgramFee, taxPercentageValue).toString(),
      );
      setValue(taxAmountWithFormatKey, calculatedAmount);
      setValue(taxAmountKey, calculatedAmount);
    }

    // Update all tax entries with recalculated amounts and formatted values
    if (isNotEmpty(filteredResults)) {
      const updatedResults = filteredResults.map((item) => {
        const updatedAmount = getValueWith2DecimalDigits(
          getValueByPercentage(
            updatedProgramFee,
            item.percentage ?? 0,
          ).toString(),
        );
        return {
          ...item,
          amount: updatedAmount,
          amountWithFormat: getAmountWithFormatByCurrencyName(
            Number(updatedAmount),
            currencyName,
          ),
        };
      });

      setFilteredResults(updatedResults);
      addEntriesToFormKey(updatedResults);
    }
  };

  const validateField = (fieldValue: string | number, fieldKey: string) => {
    if (isEmpty(fieldValue)) {
      setError(fieldKey, {
        type: "custom",
        message: fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
      });
    }
  };

  const addTaxConfiguration = () => {
    let newTaxEntry: TaxConfigureTableData | null = null;
    newTaxEntry = checkValidations();

    if (newTaxEntry) {
      clearInputValues();
      const filteredResultsCopy = [...filteredResults, newTaxEntry];
      sortFilterData(filteredResultsCopy);
      setFilteredResults(filteredResultsCopy);
      addEntriesToFormKey(filteredResultsCopy);
      setOnDataAddedCallback(!onDataAddedCallback);
    }
  };

  const clearInputValues = () => {
    setValue(taxKey, "");
    setValue(taxPercentageKey, "");
    setValue(taxAmountWithFormatKey, "");
  };

  const handleModalClose = () => {
    if (showDeleteTaxModal) {
      setShowDeleteTaxModal(false);
    }
  };

  const handleDeleteTaxClick = () => {
    const selectedTax = selectedDeleteTaxRef.current;
    if (selectedTax) {
      setShowDeleteTaxModal(false);
      handleTaxDeleteClick(selectedTax);
    }
  };

  const deleteTaxAction = (row: TaxConfigureTableData) => {
    if (!showDeleteTaxModal) {
      selectedDeleteTaxRef.current = row;
      setShowDeleteTaxModal(true);
    }
  };

  const handleTaxConfigurationAction = (row: TaxConfigureTableData) => {
    if (isInEditMode) {
      deleteTaxAction(row);
    } else {
      handleTaxDeleteClick(row);
    }
  };

  return (
    <TaxDetailsWrapper data-testid="tax-details">
      <TaxDetailsTable
        filteredResults={filteredResults}
        currencyFormat={currencyFormat}
        currencyId={currencyId}
        currencyName={currencyName}
        addTaxConfiguration={addTaxConfiguration}
        onDeleteTaxConfigurationEntryAction={handleTaxConfigurationAction}
        onTaxAddedCallback={onDataAddedCallback}
        taxList={taxList}
        onTaxDeletedCallback={onDataDeletedCallback}
        pageId={pageId}
        taxDetailsFormKeys={taxDetailsFormKeys}
        programFee={programOrProjectFee}
        isInViewMode={isInViewMode}
        hasDeletePermission={hasDeletePermission}
      />
      {showDeleteTaxModal && (
        <WarningConfirmationModal
          show={showDeleteTaxModal}
          modalTitle={commonLabels.confirmation}
          secondaryButtonTitle={commonLabels.no}
          onClose={handleModalClose}
          onPrimaryButtonClick={handleDeleteTaxClick}
          labelContent={commonTaxDetails.areYouSureYouWantToDeleteTheTax}
        />
      )}
    </TaxDetailsWrapper>
  );
});
